import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import SubHeader from '../../../layout/components/SubHeader';
import {FAQ_CATEGORIES, getFaqItems, getSupportOptions} from './smsHelpContent';
import {useEyeconxStore} from '../../../../store/eyeconxStore';
import smsgatewayService from '../../../../services/smsgateway.service';
import './SmsHelp.css';

function FaqItem({item, isOpen, onToggle}) {
    return (
        <div className="sms-help-faq-item">
            <button
                type="button"
                className="sms-help-faq-button"
                aria-expanded={isOpen}
                onClick={onToggle}
            >
                <i className="bi bi-question-circle text-primary flex-shrink-0 mt-1"/>
                <span className="flex-grow-1">{item.question}</span>
                <i className="bi bi-chevron-down faq-chevron"/>
            </button>
            {isOpen && (
                <div className="sms-help-faq-body">{item.answer}</div>
            )}
        </div>
    );
}

function SupportCard({option}) {
    const iconBgClass = `bg-${option.color} bg-opacity-10 text-${option.color}`;

    const action = option.external ? (
        <a
            href={option.href}
            className={`btn btn-sm btn-${option.color} text-white`}
            target="_blank"
            rel="noopener noreferrer"
        >
            {option.actionLabel}
            <i className="bi bi-box-arrow-up-right ms-1"/>
        </a>
    ) : (
        <Link to={option.to} className={`btn btn-sm btn-${option.color} text-white`}>
            {option.actionLabel}
            <i className="bi bi-arrow-right ms-1"/>
        </Link>
    );

    return (
        <div className="card border-0 shadow-sm sms-help-support-card h-100">
            <div className="card-body d-flex flex-column">
                <div className="sms-help-support-header d-flex align-items-center gap-3">
                    <div className={`sms-help-support-icon flex-shrink-0 ${iconBgClass}`}>
                        <i className={`bi ${option.icon}`}/>
                    </div>
                    <div className="sms-help-support-text min-w-0">
                        <h6 className="fw-semibold mb-0 sms-help-support-title">{option.title}</h6>
                        <span className="text-muted small sms-help-support-description">{option.description}</span>
                    </div>
                </div>
                <div className="sms-help-support-action mt-auto pt-3">
                    {action}
                </div>
            </div>
        </div>
    );
}

function SmsHelp() {
    const clinicId = useEyeconxStore(state => state.user.clinicId);
    const [activeSection, setActiveSection] = useState('faqs');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [openFaqId, setOpenFaqId] = useState(null);
    const [isLegacyTwoWayText, setIsLegacyTwoWayText] = useState(false);

    useEffect(() => {
        if (!clinicId) {
            setIsLegacyTwoWayText(false);
            return;
        }

        smsgatewayService.getClinicCustomize(clinicId)
            .then(res => {
                const legacySetting = (res.data || []).find(item => item.optionKey === 'isLegacyTwoWayText');
                setIsLegacyTwoWayText(legacySetting?.optionValue === '1');
            })
            .catch(() => {
                setIsLegacyTwoWayText(false);
            });
    }, [clinicId]);

    const faqItems = useMemo(
        () => getFaqItems(isLegacyTwoWayText),
        [isLegacyTwoWayText]
    );

    const supportOptions = useMemo(
        () => getSupportOptions(isLegacyTwoWayText),
        [isLegacyTwoWayText]
    );

    const filteredFaqs = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return faqItems.filter((item) => {
            const matchesCategory = categoryFilter === 'all' || item.categoryId === categoryFilter;
            if (!matchesCategory) {
                return false;
            }
            if (!query) {
                return true;
            }
            return (
                item.question.toLowerCase().includes(query)
                || item.answer.toLowerCase().includes(query)
            );
        });
    }, [categoryFilter, searchQuery, faqItems]);

    const faqsByCategory = useMemo(() => {
        if (categoryFilter !== 'all' || searchQuery.trim()) {
            return null;
        }
        return FAQ_CATEGORIES.map((cat) => ({
            ...cat,
            items: faqItems.filter((f) => f.categoryId === cat.id),
        })).filter((g) => g.items.length > 0);
    }, [categoryFilter, searchQuery, faqItems]);

    const toggleFaq = (id) => {
        setOpenFaqId((prev) => (prev === id ? null : id));
    };

    const getCategoryMeta = (categoryId) =>
        FAQ_CATEGORIES.find((c) => c.id === categoryId);

    return (
        <>
            <SubHeader
                title="Help & Support"
                description="FAQs, guides, and contact options for EyeconX SMS Gateway"
                icon="bi-life-preserver"
                iconClass="text-primary"
                containerClass="container-fluid px-4"
            />

            <div className="container-fluid px-4 py-3 sms-help-page">
                <ul className="nav nav-pills mb-4 gap-2 sms-help-nav">
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeSection === 'faqs' ? 'active' : ''}`}
                            onClick={() => setActiveSection('faqs')}
                        >
                            <i className="bi bi-journal-text me-2"/>
                            FAQs
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeSection === 'support' ? 'active' : ''}`}
                            onClick={() => setActiveSection('support')}
                        >
                            <i className="bi bi-life-preserver me-2"/>
                            Get Support
                        </button>
                    </li>
                </ul>

                {activeSection === 'faqs' && (
                    <div className="row g-3">
                        <div className="col-12 col-lg-3">
                            <div className="card shadow-sm border-0">
                                <div className="card-header bg-white py-3">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="bi bi-funnel me-2 text-primary"/>
                                        Categories
                                    </h6>
                                </div>
                                <div className="card-body p-2">
                                    <div className="nav flex-column nav-pills sms-help-nav">
                                        <button
                                            type="button"
                                            className={`nav-link text-start ${categoryFilter === 'all' ? 'active' : ''}`}
                                            onClick={() => setCategoryFilter('all')}
                                        >
                                            All topics
                                        </button>
                                        {FAQ_CATEGORIES.map((cat) => (
                                            <button
                                                key={cat.id}
                                                type="button"
                                                className={`nav-link text-start ${categoryFilter === cat.id ? 'active' : ''}`}
                                                onClick={() => setCategoryFilter(cat.id)}
                                            >
                                                <i className={`bi ${cat.icon} me-2 ${cat.color}`}/>
                                                {cat.title}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-9">
                            <div className="card shadow-sm border-0">
                                <div className="card-header bg-white py-3">
                                    <div className="row g-2 align-items-center">
                                        <div className="col">
                                            <h6 className="mb-0 fw-semibold">
                                                <i className="bi bi-patch-question me-2 text-primary"/>
                                                Frequently Asked Questions
                                            </h6>
                                        </div>
                                        <div className="col-12 col-md-auto">
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-light border-end-0">
                                                    <i className="bi bi-search text-muted"/>
                                                </span>
                                                <input
                                                    type="search"
                                                    className="form-control border-start-0 bg-light"
                                                    placeholder="Search FAQs..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                {searchQuery && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => setSearchQuery('')}
                                                        aria-label="Clear search"
                                                    >
                                                        <i className="bi bi-x-lg"/>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {filteredFaqs.length === 0 ? (
                                        <div className="sms-help-empty">
                                            <i className="bi bi-search fs-2 d-block mb-2"/>
                                            No FAQs match your search. Try another keyword or category.
                                        </div>
                                    ) : faqsByCategory ? (
                                        faqsByCategory.map((group) => (
                                            <div key={group.id} className="mb-4">
                                                <div className="d-flex align-items-center mb-2">
                                                    <i className={`bi ${group.icon} me-2 ${group.color}`}/>
                                                    <h6 className="mb-0 fw-bold">{group.title}</h6>
                                                    <span className="badge bg-light text-muted ms-2 sms-help-category-badge">
                                                        {group.items.length}
                                                    </span>
                                                </div>
                                                {group.items.map((item) => (
                                                    <FaqItem
                                                        key={item.id}
                                                        item={item}
                                                        isOpen={openFaqId === item.id}
                                                        onToggle={() => toggleFaq(item.id)}
                                                    />
                                                ))}
                                            </div>
                                        ))
                                    ) : (
                                        filteredFaqs.map((item) => {
                                            const cat = getCategoryMeta(item.categoryId);
                                            return (
                                                <div key={item.id} className="mb-3">
                                                    {cat && (
                                                        <span className={`badge bg-light ${cat.color} mb-2 sms-help-category-badge`}>
                                                            {cat.title}
                                                        </span>
                                                    )}
                                                    <FaqItem
                                                        item={item}
                                                        isOpen={openFaqId === item.id}
                                                        onToggle={() => toggleFaq(item.id)}
                                                    />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeSection === 'support' && (
                    <>
                        <div className="card shadow-sm border-0 mb-4 sms-help-intro-card">
                            <div className="card-body">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="sms-help-support-icon flex-shrink-0 bg-primary bg-opacity-10 text-primary">
                                        <i className="bi bi-life-preserver"/>
                                    </div>
                                    <div>
                                        <h5 className="fw-semibold mb-1">Need more help?</h5>
                                        <p className="text-muted mb-0 small">
                                            Use the options below to contact EyeconX support or jump directly
                                            to the part of SMS Gateway related to your question. For urgent
                                            delivery or login issues, email support with your clinic name and
                                            SMS number.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-3 sms-help-support-grid">
                            {supportOptions.map((option) => (
                                <div key={option.id} className="col-12 col-md-6 col-xl-4">
                                    <SupportCard option={option}/>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default SmsHelp;
